import { FC, useEffect, useState } from "react";
import { Modal } from "../../../../components/common/Modal/Modal";
import { ModalBody } from "../../../../components/common/Modal/ModalBody";
import { useSpentTypeStore } from "../../../../hooks/useSpentTypeStore";
import { useConceptCategoryStore } from "../../../../hooks/useConceptCategoryStore";
import { usePaymentConceptStore } from "../../../../hooks/usePaymentConceptStore";
import { Formik } from "formik";
import { Autocomplete, Divider, FormLabel, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { ConceptCategory } from "../../../../types/slices/conceptCategoryType";
import { SpentType } from "../../../../types/slices/spentTypeType";
import { CustomSnackbar } from "../../../../components/common/CustomSnackbar/CustomSnackbar";
import { MODE_BUILDING_OWNER } from "../../../../toolbox/defaults/app";
import { CustomFormFooter } from "../../../../components/common/CustomForm/CustomFormFooter";

interface SpentFormModalProps {
    open: boolean
    onClose: () => void
    onList: () => Promise<void>
}

type SpentFormData = {
    description: string
    idconcept_category: number | undefined
    idspent_type: number | undefined
    amount: number | undefined
}

export const SpentFormModal: FC<SpentFormModalProps> = ({
    open,
    onClose,
    onList
}) => {

    const { createPaymentConcept } = usePaymentConceptStore()
    const { spentTypes, getSpentTypes } = useSpentTypeStore()
    const { conceptCategories, getConceptCategories } = useConceptCategoryStore()

    const [isEdit, setIsEdit] = useState<boolean>(false)
    const [selectedSpentType, setSelectedSpentType] = useState<SpentType | null>(null)
    const [selectedCategory, setSelectedCategory] = useState<ConceptCategory | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [data, setData] = useState<SpentFormData>({
        description: '',
        idconcept_category: undefined,
        idspent_type: undefined,
        amount: undefined
    })

    useEffect(() => {
        getDataAPI()
    }, [])

    const getDataAPI = async () => {
        setLoading(true)
        await getSpentTypes()
        await getConceptCategories()
        setLoading(false)
    }

    const validateForm = (values) => {
        let errors: any = {};

        if (!values.description) errors.description = "el campo es requerido"

        return errors;
    }

    const onSubmit = async (values) => {
        if (!selectedSpentType) return CustomSnackbar('warning', 'Seleccione un tipo de gasto.')
        if (!selectedCategory) return CustomSnackbar('warning', 'Seleccione una categoría.')

        const response = await createPaymentConcept({
            ...values,
            idlevel: MODE_BUILDING_OWNER === "building" ? 1 : 2,
            idspent_type: selectedSpentType?.id,
            idconcept_category: selectedCategory?.id
        })

        if (response === true) {
            onClose()
            await onList()
        }
    }

    return (
        <Modal open={open} handleClose={onClose} disableEscapeKeyDown disableBackdropClick size="sm">
            <ModalBody>
                <Typography sx={{ display: 'flex', justifyContent: 'center', fontWeight: 'bolder' }}>
                    {"NUEVO CONCEPTO DE GASTO"}
                </Typography>
                <Formik initialValues={data} enableReinitialize validate={(values) => validateForm(values)} onSubmit={onSubmit}>
                    {({ values, errors, touched, handleSubmit, handleChange, setFieldValue }) => {
                        return (
                            <form onSubmit={handleSubmit}>
                                <Grid container sx={{ padding: '20px' }}>
                                    <Grid item container xs={12} spacing={2}>
                                        <Grid item xs={12} >
                                            <FormLabel>Descripción:</FormLabel>
                                            <TextField
                                                id="description"
                                                type="text"
                                                name="description"
                                                fullWidth
                                                size="small"
                                                value={values.description}
                                                onChange={handleChange}
                                                error={errors.description && touched.description ? true : false}
                                                helperText={errors.description && touched.description ? errors.description : ''}
                                            />
                                        </Grid>
                                        <Grid item xs={12} >
                                            <FormLabel>Categoría:</FormLabel>
                                            <Autocomplete
                                                disablePortal
                                                id="combo-box-category"
                                                value={selectedCategory}
                                                options={conceptCategories || []}
                                                getOptionLabel={(option) => option.name || ""}
                                                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                                                fullWidth
                                                renderInput={(params) => <TextField {...params} size="small" />}
                                                onChange={(e, newValue) => {
                                                    setSelectedCategory(newValue);
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} >
                                            <FormLabel>Tipo de gasto:</FormLabel>
                                            <Autocomplete
                                                disablePortal
                                                id="combo-box-spent-type"
                                                value={selectedSpentType}
                                                options={spentTypes || []}
                                                getOptionLabel={(option) => option.name || ""}
                                                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                                                fullWidth
                                                renderInput={(params) => <TextField {...params} size="small" />}
                                                onChange={(e, newValue) => {
                                                    setSelectedSpentType(newValue);
                                                }}
                                            />
                                        </Grid>
                                        {
                                            (selectedSpentType?.id == 1 && !isEdit) && (
                                                <Grid item xs={12} >
                                                    <FormLabel>Monto:</FormLabel>
                                                    <TextField
                                                        id="amount"
                                                        type="number"
                                                        name="amount"
                                                        fullWidth
                                                        size="small"
                                                        value={values.amount}
                                                        onChange={handleChange}
                                                        error={errors.amount && touched.amount ? true : false}
                                                        helperText={errors.amount && touched.amount ? errors.amount : ''}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                            inputProps: {
                                                                min: 0
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            )
                                        }
                                    </Grid>
                                </Grid>

                                <Divider />
                                <CustomFormFooter
                                    buttonType="submit"
                                    confirmText={!isEdit ? 'Guardar' : 'Actualizar'}
                                    cancelText={"Cancelar"}
                                    onConfirm={handleSubmit}
                                    onCancel={onClose}
                                />
                            </form>
                        )
                    }}
                </Formik>
            </ModalBody>
        </Modal>
    )
}